import { FC, useState } from "react";
import toast from "react-hot-toast";
import { IAIContentResponseDto } from "../../../interfaces/endpoints/IAIContent";
import { generateAIContent } from "../../../services/AIContent.service";
import Button from "../../ui/Button";
import Modal from "../../ui/Modal";
import SpinnerLoader from "../../ui/SpinnerLoader";

interface AIGenerateModalProps {
  show: boolean;
  onClose: () => void;
  entityId: number;
  pageType: number; // 0 for LinePage
  existingSections: any[]; // Pass current sections
  onGenerated: (response: IAIContentResponseDto) => void;
}

const AIGenerateModal: FC<AIGenerateModalProps> = ({
  show,
  onClose,
  entityId,
  pageType,
  existingSections,
  onGenerated
}) => {
  const [promptOverride, setPromptOverride] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await generateAIContent({
        entityId,
        pageType,
        languageIds: [1, 2], // Default to English and Albanian
        promptOverride: promptOverride.trim() || undefined,
        existingSections: existingSections.map((s) => ({
          sectionType: "", // We can infer or just leave empty if not strictly needed
          locales: s.locales.map((l: any) => ({
            languageId: l.languageId,
            title: l.title,
            content: l.content
          })),
          suggestedImageKeywords: []
        }))
      });

      if (response.data) {
        onGenerated(response.data);
        toast.success("Përmbajtja u gjenerua me sukses me AI.");
        onClose();
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      toast.error("Ndodhi një gabim gjatë gjenerimit me AI.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Gjenero me AI"
      position="center"
      modalWidth="500px"
      buttonElement={
        <Button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full md:w-auto"
        >
          {loading ? <SpinnerLoader size={20} className="text-white" /> : "Gjenero"}
        </Button>
      }
    >
      <div className="p-4">
        <p className="text-sm text-gray-600 mb-4">
          AI do të gjenerojë seksione të reja për këtë faqe bazuar në statistikat dhe të dhënat ekzistuese.
          Ju mund të shtoni udhëzime shtesë më poshtë.
        </p>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700">
            Udhëzime shtesë (Opsionale)
          </label>
          <textarea
            className="w-full p-2 border rounded-md focus:ring-primary focus:border-primary min-h-[100px]"
            placeholder="Psh: Përdor një ton më aventuresk, ose thekso çmimet e lira..."
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
            disabled={loading}
          />
        </div>
        {loading && (
          <div className="mt-4 flex flex-col items-center justify-center gap-2">
            <SpinnerLoader className="text-primary" />
            <p className="text-xs text-gray-500 animate-pulse">
              Duke analizuar të dhënat dhe duke gjeneruar përmbajtjen...
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AIGenerateModal;
